import { Dispatch, SetStateAction, useMemo } from 'react';
import { Card, CardContent, Typography, Box, Divider, Button, Tooltip } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ErrorIcon from '@mui/icons-material/Error';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getStartOfDayAsMicros, getEndOfDayAsMicros } from '@/utilities/timeUtils';
import {
  ETransportFreightPlutoDto,
  ETransportFreightPlutoDtoSource,
  ETransportTargetFreightPlutoDto,
  MinimalTarget,
} from '@/generated/model';
import { useTranslation } from 'react-i18next';
import { fullDateNoYearNoSeconds } from '@/utilities/dateUtils';
import { EtransportAutomaticIcon, EtransportManualIcon } from './utils/EtransportSvgIcons';
import { FreightDataChange } from '@/utilities/etransportTypes';
import { getPlateNumber, isClosedTarget } from './utils/utils';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

import './etransport.css';

export function FreightCard({
  active,
  freight,
  targets,
  startTime,
  setNewChanges,
  setCurrentFreight,
  setOpenFreightSubmitForm,
  setOpenTargetFreightDetails,
  setOpenTargetFreightSubmitForm,
}: {
  active: boolean;
  freight: ETransportFreightPlutoDto;
  targets: MinimalTarget[] | undefined;
  startTime: number;
  setNewChanges: Dispatch<SetStateAction<FreightDataChange | null>>;
  setCurrentFreight: Dispatch<SetStateAction<ETransportFreightPlutoDto | undefined>>;
  setOpenFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
  setOpenTargetFreightDetails: Dispatch<SetStateAction<boolean>>;
  setOpenTargetFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
}) {
  const { t, i18n } = useTranslation();
  const isManual: boolean = freight.source === ETransportFreightPlutoDtoSource.MANUAL;
  const isAutomatic: boolean = freight.source === ETransportFreightPlutoDtoSource.AUTOMATIC;
  const isSyncInProcess: boolean = (freight?.isSynchronized || false) && isManual;

  function handleOpen(): void {
    setNewChanges(null);
    setCurrentFreight(freight);
    setOpenFreightSubmitForm(false);
    setOpenTargetFreightDetails(true);
    setOpenTargetFreightSubmitForm(false);
  }

  function isFutureTarget(startTime: number, validityStartTimeMicros: number | undefined): boolean {
    const todayEnd = getEndOfDayAsMicros(startTime * 1000);
    const validityStartDay = getStartOfDayAsMicros(validityStartTimeMicros);
    return validityStartDay > todayEnd;
  }

  const topLabelColor: string = useMemo(() => {
    if (isClosedTarget(startTime, freight.validityEndTimeMicros)) return 'rgb(114, 101, 101)';
    if (isFutureTarget(startTime, freight.validityStartTimeMicros)) return 'var(--mui-palette-primary-main)';
    return 'var(--mui-palette-secondary-main)';
  }, [startTime, freight.validityEndTimeMicros, freight.validityStartTimeMicros]);

  const bgColor: string = useMemo(() => {
    if (active) {
      if (isClosedTarget(startTime, freight.validityEndTimeMicros)) return 'rgb(202, 202, 202)';
      if (isFutureTarget(startTime, freight.validityStartTimeMicros)) return 'rgb(225, 227, 236)';
      return 'rgb(248, 242, 236)';
    }
    return 'rgb(255, 255, 255)';
  }, [active, startTime, freight.validityEndTimeMicros, freight.validityStartTimeMicros]);

  const mostRecentTargetFreight: ETransportTargetFreightPlutoDto | null = useMemo(() => {
    const freights = freight.targetFreights;
    if (freights === undefined || freights.length === 0) return null;

    const now = startTime * 1000;
    const f = freights.reduce((closest, current) => {
      if (closest.startTimeMicros === undefined || current.startTimeMicros === undefined) return current;
      const closestDiff = Math.abs(closest.startTimeMicros - now);
      const currentDiff = Math.abs(current.startTimeMicros - now);
      return currentDiff < closestDiff ? current : closest;
    });
    return f;
  }, [startTime, freight.targetFreights]);

  return (
    <Card
      className={`freight-card ${isPhoneOrHighScreen() ? 'phone' : ''}`}
      onClick={handleOpen}
      sx={{
        backgroundColor: bgColor,
        cursor: 'pointer',
        '&::before': {
          backgroundColor: topLabelColor,
        },
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.21)',
      }}
    >
      <CardContent sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column', '&:last-child': { pb: 1 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="h6" component="div" color="primary" sx={{ fontWeight: 600 }}>
            {freight.uit}
          </Typography>
          {isAutomatic && (
            <Tooltip title={t('etransport.card.automatic')} arrow>
              <EtransportAutomaticIcon color="grey" backgroundColor="white" />
            </Tooltip>
          )}
          {isManual && !isSyncInProcess && (
            <Tooltip title={t('etransport.card.manual')} arrow>
              <EtransportManualIcon color="white" backgroundColor="var(--mui-palette-primary-main)" />
            </Tooltip>
          )}
          {isManual && isSyncInProcess && (
            <Tooltip title={t('etransport.card.syncinprocess')} arrow>
              <ErrorIcon color="warning" />
            </Tooltip>
          )}
        </Box>

        <Box display="flex" flexDirection="column" gap={0.5} mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.3}>
            <ApartmentIcon sx={{ fontSize: '0.9rem' }} color="action" />
            <Typography variant="body2" color="text.secondary">
              {freight.vat}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.3}>
            <AccessTimeIcon sx={{ fontSize: '0.9rem' }} color="action" />
            <Typography variant="body2" color="text.secondary">
              {fullDateNoYearNoSeconds(freight.validityStartTimeMicros || 0, i18n.language, t)} {' - '}
              {fullDateNoYearNoSeconds(freight.validityEndTimeMicros || 0, i18n.language, t)}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.3}>
            <CloudUploadIcon sx={{ fontSize: '0.9rem' }} color="action" />
            <Typography variant="body2" color="text.secondary">
              {freight.lastSentTimeMicros !== undefined && freight.lastSentTimeMicros > 0
                ? fullDateNoYearNoSeconds(freight.lastSentTimeMicros || 0, i18n.language, t)
                : 'N/A'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 0.5 }} />

        <Box mb={0.5}>
          {mostRecentTargetFreight ? (
            <Box>
              <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                <LocalShippingOutlinedIcon sx={{ fontSize: '0.9rem' }} color="action" />
                <Typography variant="body2" color="secondary" sx={{ fontWeight: 600 }}>
                  {getPlateNumber(targets, mostRecentTargetFreight.targetId || 0)}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                <LocalShippingIcon sx={{ fontSize: '0.9rem' }} color="action" />
                {mostRecentTargetFreight.trailer1 || mostRecentTargetFreight.trailer2 ? (
                  <Typography variant="body2" color="text.primary">
                    {mostRecentTargetFreight.trailer1 || 'N/A'}
                    {mostRecentTargetFreight.trailer2 && `, ${mostRecentTargetFreight.trailer2}`}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.primary">
                    N/A
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              {t('etransport.card.nodetails')}
            </Typography>
          )}
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto" gap={0.5}>
          <Button
            size="small"
            startIcon={<VisibilityIcon sx={{ fontSize: '0.9rem' }} />}
            onClick={handleOpen}
            variant="outlined"
            sx={{ fontSize: '0.7rem', py: 0.3, px: 0.8, minWidth: 'auto' }}
          >
            {t('etransport.card.details')} ({freight?.targetFreights?.length})
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
